/**
 * Help API Module
 * API functions for Visual Help System
 * @module api/help
 */

import { apiRequest } from './core.js';

// =============================================================================
// HELP API
// =============================================================================

export const helpAPI = {
  // Keyboard shortcuts
  async getShortcuts() {
    try {
      const response = await apiRequest('/help/shortcuts');
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // Help articles
  async getArticles(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString ? `/help/articles?${queryString}` : '/help/articles';
      const response = await apiRequest(endpoint);
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  async getArticle(slug) {
    try {
      const response = await apiRequest(`/help/articles/${slug}`);
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  async getFeaturedArticles() {
    try {
      const response = await apiRequest('/help/articles/featured');
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  async getArticlesByCategory(category) {
    try {
      const response = await apiRequest(`/help/articles/category/${category}`);
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // Track article view
  async trackArticleView(slug) {
    try {
      await apiRequest(`/help/articles/${slug}/view`, { method: 'POST' });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // Log search
  async logSearch(query, resultsCount) {
    try {
      await apiRequest('/help/search/log', {
        method: 'POST',
        body: JSON.stringify({ query, results_count: resultsCount }),
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // ==========================================================================
  // ONBOARDING CHECKLIST
  // ==========================================================================

  /**
   * Get onboarding checklist with user progress
   * @returns {Promise<{ok: boolean, data?: {tasks: Array, completed: number, total: number, percentage: number, dismissed: boolean}}>}
   */
  async getOnboarding() {
    try {
      const response = await apiRequest('/help/onboarding');
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Mark an onboarding task as complete
   * @param {string} taskCode - The task code to complete
   */
  async completeOnboardingTask(taskCode) {
    try {
      await apiRequest(`/help/onboarding/${taskCode}/complete`, { method: 'POST' });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Dismiss the onboarding checklist permanently
   */
  async dismissOnboarding() {
    try {
      await apiRequest('/help/onboarding/dismiss', { method: 'POST' });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Reset the onboarding checklist for a user (admin action)
   * @param {number} [userId] - Target user ID. If omitted, resets for current user.
   */
  async resetOnboarding(userId) {
    try {
      await apiRequest('/help/onboarding/reset', {
        method: 'POST',
        body: JSON.stringify(userId ? { userId } : {}),
      });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // ==========================================================================
  // QUICK ACTIONS (per-view help panel actions)
  // ==========================================================================

  /**
   * Get all quick actions grouped by view
   * @returns {Promise<{ok: boolean, data?: {actions: Array, grouped: Object}}>}
   */
  async getQuickActions() {
    try {
      const response = await apiRequest('/help/quick-actions');
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Get quick actions for a specific view
   * @param {string} viewCode - The view code (dashboard, vehicles, drivers, etc.)
   * @returns {Promise<{ok: boolean, data?: Array}>}
   */
  async getQuickActionsForView(viewCode) {
    try {
      const response = await apiRequest(`/help/quick-actions/${viewCode}`);
      return { ok: true, data: response };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },
};

export default helpAPI;
